'use client';
import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────
type ReviewTab = 'received' | 'given' | 'submit';
type ReviewerRole = 'client' | 'consultant';

interface ReviewUser {
  id: string;
  full_name: string;
  initials: string;
  role: string;
}

interface SessionReview {
  id: string;
  booking_id: string | null;
  reviewer_id: string;
  reviewee_id: string;
  reviewer_role: ReviewerRole;
  rating: number;
  feedback: string | null;
  is_visible_on_profile: boolean;
  created_at: string;
  reviewer?: ReviewUser;
  reviewee?: ReviewUser;
}

// ─── Star Rating Component ─────────────────────────────────────────────────────
function StarRating({
  value,
  onChange,
  readonly = false,
  size = 'md',
}: {
  value: number;
  onChange?: (v: number) => void;
  readonly?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  const [hovered, setHovered] = useState(0);
  const sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = (hovered || value) >= star;
        return (
          <button
            key={star}
            type="button"
            disabled={readonly}
            onClick={() => !readonly && onChange?.(star)}
            onMouseEnter={() => !readonly && setHovered(star)}
            onMouseLeave={() => !readonly && setHovered(0)}
            className={`${sizeClass} transition-colors duration-100 ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110 transition-transform'}`}
            aria-label={`${star} star${star !== 1 ? 's' : ''}`}
          >
            <svg viewBox="0 0 20 20" fill={filled ? '#f59e0b' : 'none'} stroke={filled ? '#f59e0b' : '#d1d5db'} strokeWidth="1.5">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </button>
        );
      })}
    </div>
  );
}

// ─── Review Card ───────────────────────────────────────────────────────────────
function ReviewCard({ review, perspective }: { review: SessionReview; perspective: 'received' | 'given' }) {
  const person = perspective === 'received' ? review.reviewer : review.reviewee;
  const label = perspective === 'received' ? 'From' : 'To';
  const roleLabel = review.reviewer_role === 'client' ? 'Client' : 'Consultant';
  const roleBg = review.reviewer_role === 'client' ? 'bg-blue-100 text-blue-700' : 'bg-teal-100 text-teal-700';

  const initials = person?.initials || (person?.full_name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() ?? '??');
  const name = person?.full_name ?? 'Unknown User';

  const dateStr = new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="bg-card border border-border rounded-2xl p-5 shadow-card hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full gradient-teal flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
            {initials}
          </div>
          <div>
            <p className="text-sm font-600 text-foreground">{label}: {name}</p>
            <p className="text-xs text-muted-foreground">{dateStr}</p>
          </div>
        </div>
        <span className={`text-xs font-500 px-2 py-1 rounded-full ${roleBg}`}>{roleLabel}</span>
      </div>

      <StarRating value={review.rating} readonly size="sm" />

      {review.feedback && (
        <p className="mt-3 text-sm text-foreground/80 leading-relaxed border-l-2 border-primary/30 pl-3">
          &ldquo;{review.feedback}&rdquo;
        </p>
      )}

      {!review.is_visible_on_profile && (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Icon name="EyeSlashIcon" size={14} />
          <span>Hidden from profile</span>
        </div>
      )}
    </div>
  );
}

// ─── Submit Review Form ────────────────────────────────────────────────────────
function SubmitReviewForm({ currentUserId, onSuccess }: { currentUserId: string | null; onSuccess: () => void }) {
  const supabase = createClient();
  const [reviewerRole, setReviewerRole] = useState<ReviewerRole>('client');
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [isVisible, setIsVisible] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Real completed sessions from Supabase
  const [completedSessions, setCompletedSessions] = useState<{ id: string; label: string; revieweeId: string; bookingId: string }[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [selectedSession, setSelectedSession] = useState('');

  useEffect(() => {
    async function fetchCompletedSessions() {
      if (!currentUserId) {
        setLoadingSessions(false);
        return;
      }
      setLoadingSessions(true);
      try {
        // Fetch completed bookings where user is client
        const { data: clientBookings } = await supabase
          .from('consultation_bookings')
          .select('id, service_title, provider_name, provider_user_id, booking_date, consultant_id')
          .eq('client_user_id', currentUserId)
          .eq('status', 'completed');

        // Fetch completed bookings where user is provider
        const { data: providerBookings } = await supabase
          .from('consultation_bookings')
          .select('id, service_title, client_name, client_user_id, booking_date')
          .eq('provider_user_id', currentUserId)
          .eq('status', 'completed');

        const sessions: { id: string; label: string; revieweeId: string; bookingId: string }[] = [];

        (clientBookings ?? []).forEach((b) => {
          const dateStr = b.booking_date ? new Date(b.booking_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
          sessions.push({
            id: `client-${b.id}`,
            label: `${b.provider_name ?? 'Consultant'} — ${b.service_title ?? 'Session'} (${dateStr})`,
            revieweeId: b.provider_user_id ?? '',
            bookingId: b.id,
          });
        });

        (providerBookings ?? []).forEach((b) => {
          const dateStr = b.booking_date ? new Date(b.booking_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
          sessions.push({
            id: `provider-${b.id}`,
            label: `${b.client_name ?? 'Client'} — ${b.service_title ?? 'Session'} (${dateStr})`,
            revieweeId: b.client_user_id ?? '',
            bookingId: b.id,
          });
        });

        setCompletedSessions(sessions);
      } catch {
        // silently fail — form still usable
      } finally {
        setLoadingSessions(false);
      }
    }

    fetchCompletedSessions();
  }, [currentUserId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUserId) { setError('You must be signed in to submit a review.'); return; }
    if (rating === 0) { setError('Please select a star rating.'); return; }
    if (!selectedSession) { setError('Please select a completed session.'); return; }

    setSubmitting(true);
    setError('');

    const session = completedSessions.find((s) => s.id === selectedSession);
    if (!session) { setError('Invalid session selected.'); setSubmitting(false); return; }

    const { error: insertError } = await supabase.from('session_reviews').insert({
      booking_id: session.bookingId,
      reviewer_id: currentUserId,
      reviewee_id: session.revieweeId || currentUserId,
      reviewer_role: reviewerRole,
      rating,
      feedback: feedback.trim() || null,
      is_visible_on_profile: isVisible,
    });

    setSubmitting(false);
    if (insertError) {
      setError(insertError.message);
    } else {
      setSuccess(true);
      setRating(0);
      setFeedback('');
      setSelectedSession('');
      onSuccess();
      setTimeout(() => setSuccess(false), 4000);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Session Selector */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-2">Completed Session</label>
        {loadingSessions ? (
          <div className="w-full bg-muted border border-border rounded-xl px-4 py-3 text-sm text-muted-foreground animate-pulse">
            Loading sessions…
          </div>
        ) : (
          <select
            value={selectedSession}
            onChange={(e) => setSelectedSession(e.target.value)}
            className="w-full bg-muted border border-border rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <option value="">Select a session to review…</option>
            {completedSessions.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        )}
        {!loadingSessions && completedSessions.length === 0 && (
          <p className="text-xs text-muted-foreground mt-1.5">No completed sessions found. Sessions become reviewable after completion.</p>
        )}
      </div>

      {/* Role */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-2">Your Role in This Session</label>
        <div className="flex gap-3">
          {(['client', 'consultant'] as ReviewerRole[]).map((role) => (
            <button
              key={role}
              type="button"
              onClick={() => setReviewerRole(role)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-500 border transition-all duration-150 ${
                reviewerRole === role
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted text-muted-foreground border-border hover:border-primary/40'
              }`}
            >
              {role.charAt(0).toUpperCase() + role.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Star Rating */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-2">
          Overall Rating <span className="text-destructive">*</span>
        </label>
        <div className="flex items-center gap-4">
          <StarRating value={rating} onChange={setRating} size="lg" />
          {rating > 0 && (
            <span className="text-sm text-muted-foreground">
              {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][rating]}
            </span>
          )}
        </div>
      </div>

      {/* Feedback */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-2">Written Feedback <span className="text-muted-foreground font-400">(optional)</span></label>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          rows={4}
          maxLength={1000}
          placeholder="Share your experience — what went well, what could be improved…"
          className="w-full bg-muted border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
        />
        <p className="text-xs text-muted-foreground mt-1 text-right">{feedback.length}/1000</p>
      </div>

      {/* Visibility Toggle */}
      <div className="flex items-center justify-between bg-muted rounded-xl px-4 py-3">
        <div>
          <p className="text-sm font-600 text-foreground">Show on Profile</p>
          <p className="text-xs text-muted-foreground">Make this review visible on the consultant&apos;s public profile</p>
        </div>
        <button
          type="button"
          onClick={() => setIsVisible(!isVisible)}
          className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${isVisible ? 'bg-primary' : 'bg-border'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${isVisible ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </div>

      {/* Error / Success */}
      {error && (
        <div className="flex items-center gap-2 bg-destructive/10 text-destructive rounded-xl px-4 py-3 text-sm">
          <Icon name="ExclamationCircleIcon" size={16} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-green-100 text-green-700 rounded-xl px-4 py-3 text-sm">
          <Icon name="CheckCircleIcon" size={16} />
          <span>Review submitted successfully!</span>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || rating === 0}
        className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-600 text-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-150 flex items-center justify-center gap-2"
      >
        {submitting ? (
          <>
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Submitting…
          </>
        ) : (
          <>
            <Icon name="StarIcon" size={16} />
            Submit Review
          </>
        )}
      </button>
    </form>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function SessionReviewsPage() {
  const supabase = createClient();
  const [activeTab, setActiveTab] = useState<ReviewTab>('received');
  const [reviews, setReviews] = useState<SessionReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [avgRating, setAvgRating] = useState(0);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setCurrentUserId(user.id);
      await fetchReviews(user?.id ?? null);
    };
    init();
  }, []);

  const fetchReviews = async (userId: string | null) => {
    setLoading(true);
    const { data, error } = await supabase
      .from('session_reviews')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setReviews(data as SessionReview[]);
      const received = data.filter((r) => r.reviewee_id === userId);
      if (received.length > 0) {
        const avg = received.reduce((sum, r) => sum + r.rating, 0) / received.length;
        setAvgRating(Math.round(avg * 10) / 10);
      }
    }
    setLoading(false);
  };

  const receivedReviews = reviews.filter((r) => r.reviewee_id === currentUserId);
  const givenReviews = reviews.filter((r) => r.reviewer_id === currentUserId);

  // For demo: show all reviews when no user is logged in
  const displayReceived = currentUserId ? receivedReviews : reviews.slice(0, 4);
  const displayGiven = currentUserId ? givenReviews : reviews.slice(0, 2);

  const tabs: { id: ReviewTab; label: string; icon: string; count?: number }[] = [
    { id: 'received', label: 'Reviews Received', icon: 'InboxArrowDownIcon', count: displayReceived.length },
    { id: 'given', label: 'Reviews Given', icon: 'PaperAirplaneIcon', count: displayGiven.length },
    { id: 'submit', label: 'Write a Review', icon: 'PencilSquareIcon' },
  ];

  return (
    <AppLayout activeRoute="/session-reviews">
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-card border-b border-border px-6 py-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-700 text-foreground">Session Reviews</h1>
                <p className="text-sm text-muted-foreground mt-1">Rate and review consultants and clients after completed sessions</p>
              </div>
              {avgRating > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3 text-center">
                  <p className="text-3xl font-700 text-amber-600">{avgRating}</p>
                  <StarRating value={Math.round(avgRating)} readonly size="sm" />
                  <p className="text-xs text-amber-700 mt-1">{receivedReviews.length} review{receivedReviews.length !== 1 ? 's' : ''}</p>
                </div>
              )}
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              {[
                { label: 'Reviews Received', value: displayReceived.length, icon: 'StarIcon', color: 'text-amber-500' },
                { label: 'Reviews Given', value: displayGiven.length, icon: 'PencilSquareIcon', color: 'text-primary' },
                { label: 'Avg. Rating', value: avgRating > 0 ? avgRating.toFixed(1) : '—', icon: 'ChartBarIcon', color: 'text-green-500' },
              ].map((stat) => (
                <div key={stat.label} className="bg-muted rounded-2xl px-4 py-4 flex items-center gap-3">
                  <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={20} className={stat.color} />
                  <div>
                    <p className="text-xl font-700 text-foreground">{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border bg-card">
          <div className="max-w-4xl mx-auto px-6">
            <div className="flex gap-1 overflow-x-auto scrollbar-thin">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-4 text-sm font-500 border-b-2 whitespace-nowrap transition-colors duration-150 ${
                    activeTab === tab.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={16} />
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-600 ${activeTab === tab.id ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <svg className="animate-spin w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            </div>
          ) : (
            <>
              {/* Reviews Received */}
              {activeTab === 'received' && (
                <div className="space-y-4">
                  {displayReceived.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Icon name="StarIcon" size={28} className="text-muted-foreground" />
                      </div>
                      <p className="text-foreground font-600">No reviews received yet</p>
                      <p className="text-sm text-muted-foreground mt-1">Reviews from clients and consultants will appear here after completed sessions.</p>
                    </div>
                  ) : (
                    displayReceived.map((review) => (
                      <ReviewCard key={review.id} review={review} perspective="received" />
                    ))
                  )}
                </div>
              )}

              {/* Reviews Given */}
              {activeTab === 'given' && (
                <div className="space-y-4">
                  {displayGiven.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Icon name="PencilSquareIcon" size={28} className="text-muted-foreground" />
                      </div>
                      <p className="text-foreground font-600">No reviews given yet</p>
                      <p className="text-sm text-muted-foreground mt-1">Switch to the &ldquo;Write a Review&rdquo; tab to rate a completed session.</p>
                      <button
                        onClick={() => setActiveTab('submit')}
                        className="mt-4 px-5 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-600 hover:opacity-90 transition-opacity"
                      >
                        Write Your First Review
                      </button>
                    </div>
                  ) : (
                    displayGiven.map((review) => (
                      <ReviewCard key={review.id} review={review} perspective="given" />
                    ))
                  )}
                </div>
              )}

              {/* Submit Review */}
              {activeTab === 'submit' && (
                <div className="max-w-xl">
                  <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                        <Icon name="StarIcon" size={20} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="text-base font-700 text-foreground">Write a Review</h2>
                        <p className="text-xs text-muted-foreground">Share your experience after a completed session</p>
                      </div>
                    </div>
                    <SubmitReviewForm
                      currentUserId={currentUserId}
                      onSuccess={() => fetchReviews(currentUserId)}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
